import type { MatchSetup } from '@sd/bots';
import { DEFAULT_LOCALE, hasKey, isLocale, t, type Locale } from '@sd/i18n';
import type { ProjectId } from '@sd/content';
import {
  createGame,
  enemyHexes,
  findPath,
  isArmed,
  fleetTicksPerHex,
  hexKey,
  type Command,
  type GameState,
  type Hex,
} from '@sd/sim';
import {
  AmbientLight,
  Color,
  HemisphereLight,
  PerspectiveCamera,
  PointLight,
  SRGBColorSpace,
  Scene,
  Vector3,
  WebGLRenderer,
} from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { CSS2DRenderer } from 'three/addons/renderers/CSS2DRenderer.js';
import { MatchHost, type Snapshot } from './game/host';
import {
  NetError,
  NetHost,
  RoomSession,
  createRoom,
  listRooms,
  type MatchStart,
  type RoomListing,
} from './game/net';
import { TICKS_PER_SECOND } from './game/protocol';
import { BenchMeter, benchState } from './map/bench';
import { CameraRig } from './map/cameraRig';
import { describeHex, fleetCard } from './map/describe';
import { LiveLayer } from './map/liveLayer';
import { LOBBY_SEED, battleHex, lobbyBattleState, lobbyVolley } from './map/lobbyBattle';
import { OutlineRenderer } from './outline';
import { SCENE_COLORS } from './palette';
import {
  HEX_SIZE,
  buildSystemView,
  hexToWorld,
  starLightColor,
  type SystemView,
} from './systemView';
import { clock, playerName } from './ui/format';
import { defaultChoice, renderLobby, toSetup, type LobbyChoice } from './ui/lobby';
import { renderMainMenu, renderRoom, renderRoomList } from './ui/netLobby';
import { SelectionCard, Tooltip } from './ui/mapPanels';
import { CrisisBanner } from './ui/crisisBanner';
import { FindOfferPanel } from './ui/findOffer';
import { noteFor } from './ui/events';
import { LeftColumn } from './ui/leftColumn';
import { MatchHud } from './ui/matchHud';
import { Notes } from './ui/notes';
import { SidePanel } from './ui/sidePanel';
import { UnitsBar } from './ui/unitsBar';
import { HitWatch } from './ui/hits';
import { loadControls, saveControls, type ControlScheme } from './ui/settings';
import { Minimap } from './ui/minimap';
import { HintCard, hintsOff } from './ui/hintCard';
import { summaryHtml, summaryView } from './ui/summaryModel';
import { MatchHistory } from './game/history';
import {
  fetchLeaderboard,
  goSignIn,
  leaderboardHtml,
  portalOrigin,
  sendReport,
  sendSurvey,
  serverMode,
  surveyHtml,
  whoAmI,
  type Account,
  type Leaderboard,
} from './ui/account';

/**
 * Phase 3 client (#53): the lobby, then a match against bots whose
 * simulation runs in a worker. URL parameters make views reproducible:
 *   ?seed=demo&players=4&lang=en            — the lobby with these choices
 *   ?seed=demo&players=4&lang=en&autostart=1 — straight into the match
 *   …&dev=1                                  — dev tools: speed ×1/×2/×4,
 *                                              &at=<tick> to start later, &camera=far,
 *                                              &spectate=1 (a bot plays your seat; =raider picks it),
 *                                              &speed=<n> (up to 1000, for the browser test),
 *                                              &bench=300 (render benchmark, no match)
 */

const params = new URLSearchParams(location.search);
/** Dev tools: speed ×2/×4, `at=<tick>` to start later, `camera=far` to start over the whole map. */
const dev = params.get('dev') === '1';
/** `speed=` of the dev tools: ticks per real tick, at most. */
const MAX_DEV_SPEED = 1000;
/** The player's seat: p1 against bots, the seat the server gave in a network match (#139). */
let ME = 'p1';
let locale: Locale = readLocale();
let choice: LobbyChoice = defaultChoice(
  params.get('seed') ?? 'demo',
  Math.min(4, Math.max(2, Number(params.get('players') ?? 2) || 2)),
);

function readLocale(): Locale {
  const fromUrl = params.get('lang');
  if (fromUrl && isLocale(fromUrl)) return fromUrl;
  try {
    const saved = localStorage.getItem('sd.locale');
    if (saved && isLocale(saved)) return saved;
  } catch {
    // storage can be unavailable (private mode, sandboxed previews)
  }
  return DEFAULT_LOCALE;
}

function saveLocale(value: Locale) {
  try {
    localStorage.setItem('sd.locale', value);
  } catch {
    // ignore
  }
}

// ---------- renderer, camera, controls ----------
const app = document.querySelector<HTMLDivElement>('#app')!;
const renderer = new WebGLRenderer({ antialias: true });
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.outputColorSpace = SRGBColorSpace;
app.append(renderer.domElement);

const labelRenderer = new CSS2DRenderer();
labelRenderer.domElement.className = 'labels';
app.append(labelRenderer.domElement);

const scene = new Scene();
scene.background = new Color(SCENE_COLORS.background);

const camera = new PerspectiveCamera(40, 1, 0.1, 800);
const controls = new OrbitControls(camera, labelRenderer.domElement);
controls.enableDamping = true;
controls.maxPolarAngle = 1.25; // never look from below the plane, like Civilization
// Close enough to read a planet and its fleets (#96: the author wants the camera nearer).
controls.minDistance = 4;
controls.screenSpacePanning = false;

scene.add(new AmbientLight('#8090b0', 0.9));
scene.add(new HemisphereLight('#b8c8ff', '#1a1420', 0.45));
const starLight = new PointLight('#ffffff', 3.2, 0, 0);
starLight.position.set(0, 1.2, 0);
scene.add(starLight);

const outline = new OutlineRenderer(renderer, new Color(SCENE_COLORS.outline));
const rig = new CameraRig(camera, controls, labelRenderer.domElement);
/** Mouse or touchpad (#162): saved per browser, switched in the main menu. */
let scheme: ControlScheme = loadControls();
rig.setTouchpad(scheme === 'touchpad');
/** The top bar covers this much of the map: the minimap's footprint starts below it. */
const TOPBAR_HEIGHT = 58;

const ui = document.createElement('div');
ui.className = 'sd-ui';
app.append(ui);

let view: SystemView | undefined;

/** Shows a system; the camera frames the whole map, or starts close over a capital. */
function showSystem(game: GameState, setup: MatchSetup, focus: Hex | null) {
  if (view) {
    scene.remove(view.root);
    view.dispose();
  }
  view = buildSystemView(game.system, new Map(setup.seats.map((s) => [s.id, s.faction])));
  scene.add(view.root);
  starLight.color.copy(starLightColor(game.system));
  for (const label of view.labels)
    label.element.textContent = playerName(locale, setup, label.playerId);

  const extent = game.system.mapRadius * Math.sqrt(3) * HEX_SIZE;
  const whole = (extent * 1.05) / Math.tan((camera.fov * Math.PI) / 360);
  controls.maxDistance = whole * 1.6;
  // Close like a city view in Civilization, or the whole system from above.
  const distance = focus ? 20 * HEX_SIZE : whole;
  const tilt = ((focus ? 56 : 50) * Math.PI) / 180;
  const target = focus ? hexToWorld(focus) : hexToWorld({ q: 0, r: 0 });
  controls.target.copy(target);
  camera.position.set(target.x, distance * Math.sin(tilt), target.z + distance * Math.cos(tilt));
  controls.update();
}

// ---------- lobby ----------
const freshSeed = () => Math.random().toString(36).slice(2, 8);
/** The signed-in player (#85) and the leaderboard beside the lobby, refreshed after each match. */
let account: Account | null = null;
let leaders: Leaderboard | null = null;

function showLeaders() {
  const draw = () => {
    ui.querySelector('.sd-leaders')?.remove();
    ui.insertAdjacentHTML(
      'beforeend',
      leaderboardHtml(locale, leaders, account, portalOrigin(location)),
    );
  };
  draw();
  if (!leaders) {
    void fetchLeaderboard().then((board) => {
      leaders = board;
      if (['lobby', 'menu'].includes(document.body.dataset.screen ?? '')) draw();
    });
  }
}

/**
 * The lobby's backdrop (#98): a mass battle on a fixed map, over and over —
 * never the coming match's system, which is the player's to discover.
 */
let lobbyScene: { timer: number } | null = null;
const LOBBY_SETUP: MatchSetup = {
  seed: LOBBY_SEED,
  seats: [
    { id: 'p1', faction: 'earthborn', control: 'human' },
    { id: 'p2', faction: 'reforged', control: 'raider' },
  ],
};

function showLobbyScene() {
  if (lobbyScene) return;
  const base = createGame({ seed: LOBBY_SEED, players: LOBBY_SETUP.seats });
  const center = battleHex(base);
  showSystem(base, LOBBY_SETUP, center);
  // Nobody's capital in the backdrop: it is not a map to read.
  for (const label of view?.labels ?? []) label.element.hidden = true;
  // Closer than a match starts, and lower, like a war film's wide shot.
  const target = hexToWorld(center);
  camera.position.set(target.x + 6, 7, target.z + 9);
  controls.update();
  live?.dispose();
  live = new LiveLayer(ME, locale, base.system.mapRadius, () => {});
  scene.add(live.root);
  let tick = 0;
  live.update(lobbyBattleState(base, tick));
  const timer = window.setInterval(() => {
    const state = lobbyBattleState(base, ++tick);
    live?.update(state);
    live?.fire(lobbyVolley(state));
  }, 1000 / TICKS_PER_SECOND);
  lobbyScene = { timer };
}

function hideLobbyScene() {
  if (!lobbyScene) return;
  window.clearInterval(lobbyScene.timer);
  lobbyScene = null;
  live?.dispose();
  live = null;
  dispersedFrom = null;
}

/** The screens before a match share the battle backdrop. */
function backdrop(screen: string) {
  document.documentElement.lang = locale;
  document.title = t(locale, 'game.title');
  delete document.body.dataset.ended;
  showLobbyScene();
  rig.lock(true);
  controls.autoRotate = true;
  controls.autoRotateSpeed = 0.25;
  document.body.dataset.screen = screen;
}

/** The main menu (#139): a match against bots, network rooms, the developer's site. */
function openMenu() {
  backdrop('menu');
  stopRoomList();
  renderMainMenu(
    ui,
    locale,
    serverMode,
    serverMode ? portalOrigin(location) : null,
    {
      single: openLobby,
      create: () => openFind(),
      find: () => openFind(),
      locale(next) {
        locale = next;
        saveLocale(next);
        openMenu();
      },
      controls(next) {
        scheme = next;
        saveControls(next);
        rig.setTouchpad(next === 'touchpad');
        openMenu();
      },
    },
    scheme,
  );
  if (serverMode) showLeaders();
}

// ---------- network rooms (#139) ----------
let room: RoomSession | null = null;
let netHost: NetHost | null = null;
let roomList: { timer: number } | null = null;
/** A line over the room or the list: why something failed, a link copied. */
let netNote: string | null = null;

function stopRoomList() {
  if (roomList) window.clearInterval(roomList.timer);
  roomList = null;
}

const netReason = (error: unknown) => {
  const reason = error instanceof NetError ? error.message : 'offline';
  const key = `net.error.${reason.replace(/\s+(\w)/g, (_, c: string) => c.toUpperCase())}`;
  return hasKey(key) ? t(locale, key) : t(locale, 'net.error.other', { reason });
};

/** Open rooms to join, one by its code, or a new one. */
function openFind(rooms: readonly RoomListing[] | null = null) {
  backdrop('rooms');
  const draw = (list: readonly RoomListing[] | null) =>
    renderRoomList(
      ui,
      locale,
      list,
      account ? t(locale, 'net.defaultName', { name: account.name }) : '',
      netNote,
      {
        create: (name, visibility) => {
          netNote = null;
          createRoom(name, visibility)
            .then((view) => enterRoom(view.id))
            .catch((error: unknown) => {
              netNote = netReason(error);
              openFind();
            });
        },
        join: (code) => {
          netNote = null;
          RoomSession.join(code)
            .then((view) => enterRoom(view.id))
            .catch((error: unknown) => {
              netNote = netReason(error);
              openFind();
            });
        },
        back: () => {
          netNote = null;
          openMenu();
        },
      },
    );
  draw(rooms);
  stopRoomList();
  const refresh = () =>
    void listRooms()
      .then((list) => {
        if (document.body.dataset.screen === 'rooms' && roomList) draw(list);
      })
      .catch(() => {});
  refresh();
  roomList = { timer: window.setInterval(refresh, 3000) };
}

/** In a room: its seats until the host starts, then the match. */
function enterRoom(id: string) {
  stopRoomList();
  room?.close();
  netNote = null;
  window.history.replaceState(null, '', `?room=${id}`);
  /** Who a bot plays for right now (#142): a line when that changes in a match. */
  let standIns = new Set<string>();
  const session: RoomSession = new RoomSession(id, {
    room: (view) => {
      if (document.body.dataset.screen === 'room' && view.status === 'lobby') drawRoom(session);
      const now = new Set(view.seats.flatMap((s) => (s.standIn && s.account ? [s.account] : [])));
      if (netHost) {
        for (const name of now) if (!standIns.has(name)) flash(t(locale, 'net.standIn', { name }));
        for (const name of standIns)
          if (!now.has(name)) flash(t(locale, 'net.standInBack', { name }));
      }
      standIns = now;
    },
    start: (start) => {
      if (netHost) netHost.resync(start);
      else startMatch(start.setup, { start, session });
    },
    step: (step) => netHost?.step(step),
    closed: () => {
      room = null;
      window.history.replaceState(null, '', location.pathname);
      if (document.body.dataset.screen === 'room') {
        netNote = t(locale, 'net.closed');
        openFind();
      }
    },
    offline: (down) => {
      document.querySelector('.sd-netdown')?.remove();
      if (!down) return;
      const banner = document.createElement('div');
      banner.className = 'sd-netdown';
      banner.setAttribute('role', 'status');
      banner.textContent = t(locale, 'net.reconnecting');
      document.body.append(banner);
    },
  });
  room = session;
  backdrop('room');
  ui.innerHTML = `<section class="sd-panel sd-panel--strong sd-lobby"><p class="sd-muted">${t(locale, 'net.loading')}</p></section>`;
}

/** A line over the match for a few seconds. */
function flash(text: string) {
  const line = document.createElement('div');
  line.className = 'sd-netdown sd-netdown--info';
  line.setAttribute('role', 'status');
  line.textContent = text;
  document.body.append(line);
  window.setTimeout(() => line.remove(), 6000);
}

function drawRoom(session: RoomSession) {
  const view = session.view;
  if (!view || !account) return;
  const act = (promise: Promise<unknown>) =>
    promise.catch((error: unknown) => {
      netNote = netReason(error);
      drawRoom(session);
    });
  renderRoom(ui, locale, view, account.name, netNote, {
    settings: (patch) => void act(session.settings(patch)),
    sit: (index) => void act(session.sit(index)),
    start: () => void act(session.start()),
    leave: () => {
      void session.leave();
      room = null;
      window.history.replaceState(null, '', location.pathname);
      openMenu();
    },
    copyLink: () => {
      const link = `${location.origin}${location.pathname}?room=${view.id}`;
      void navigator.clipboard?.writeText(link).catch(() => {});
      netNote = t(locale, 'net.copied', { link });
      drawRoom(session);
    },
  });
}

function openLobby() {
  backdrop('lobby');
  stopRoomList();
  renderLobby(ui, locale, choice, {
    onChange(next) {
      choice = next;
      openLobby();
    },
    onLocale(next) {
      locale = next;
      saveLocale(next);
      openLobby();
    },
    // Every match a fresh system; a fixed seed is a dev tool (#98).
    onStart: (setup) => startMatch(dev ? setup : { ...setup, seed: freshSeed() }),
    randomSeed: freshSeed,
    showSeed: dev,
    back: openMenu,
  });
  if (serverMode) showLeaders();
}

// ---------- match ----------
let host: MatchHost | NetHost | null = null;
let hud: MatchHud | null = null;
let live: LiveLayer | null = null;
/** The exploration state the map's dispersed clouds were last taken from (#103). */
let dispersedFrom: unknown = null;
/** Map panels of the running match, gone with it. */
let panels: {
  tooltip: Tooltip;
  card: SelectionCard;
  minimap: Minimap;
  left: LeftColumn;
  side: SidePanel;
  banner: CrisisBanner;
  finds: FindOfferPanel;
  hints: HintCard;
  notes: Notes;
  units: UnitsBar;
} | null = null;
/** What the player picked on the map: one of their fleets, or a hex to read about. */
let selection: {
  fleetId: number | null;
  hex: Hex;
  /** Fleets picked together with a drag (#111); the card shows the first. */
  group?: number[] | undefined;
} | null = null;
let stopMapInput: (() => void) | null = null;

function startMatch(given: MatchSetup, net?: { start: MatchStart; session: RoomSession }) {
  stopRoomList();
  ME = net?.start.you ?? 'p1';
  // The other people go by their names; the player's own seat is «Вы» (#139).
  const setup: MatchSetup = {
    ...given,
    seats: given.seats.map((s) =>
      s.id === ME
        ? {
            id: s.id,
            faction: s.faction,
            control: s.control,
            ...(s.team !== undefined ? { team: s.team } : {}),
          }
        : s,
    ),
  };
  hideLobbyScene();
  rig.lock(false);
  controls.autoRotate = false;
  ui.innerHTML = '';
  ui.onclick = null;
  ui.onchange = null;
  let shown = false;
  let lastState: GameState | null = null;
  const viewer = { me: ME, locale, setup };
  const history = new MatchHistory();
  const hits = new HitWatch();
  /** The playtest build sends the match as soon as it ends (#85); the survey follows it. */
  let reported: Promise<string | null> = Promise.resolve(null);
  let sent = false;
  const report = async (state: GameState): Promise<string | null> => {
    const result = state.result;
    if (!host || !result) return null;
    const me = state.players.find((p) => p.id === ME);
    return sendReport({
      summary: {
        seed: setup.seed,
        players: setup.seats.length,
        faction: me?.faction ?? '',
        result: result.kind,
        winner: result.winner,
        won: result.winners.includes(ME),
        place: result.standings.findIndex((x) => x.playerId === ME) + 1,
        minutes: result.tick / TICKS_PER_SECOND / 60,
        prestige: me?.prestige ?? 0,
      },
      survey: {},
      log: await host.log(),
    });
  };
  hud = new MatchHud(ui, locale, setup, ME, {
    togglePause: () => {
      host?.send({ type: lastState?.pause?.by === ME ? 'resume' : 'pause', playerId: ME });
    },
    openMenu: (open) => host?.freeze(open),
    // ×2 and ×4 are a dev tool for now (#54): ?dev=1 turns them on; a network match runs at ×1.
    ...(dev && !net ? { setSpeed: (speed: number) => host?.setSpeed(speed) } : {}),
    saveLog: () => void saveLog(setup),
    summary: (state) => {
      const view = summaryView(state, viewer, history.samples);
      if (!view) return null;
      // The playtest build asks a few questions under the result (#61, #62).
      return {
        title: view.title,
        html: summaryHtml(locale, view) + (serverMode ? surveyHtml(locale) : ''),
      };
    },
    ...(serverMode
      ? {
          submitSurvey: async (answers: Record<string, string>) => {
            const id = await reported;
            return id !== null && sendSurvey(id, answers);
          },
        }
      : {}),
    hintsOn: () => !hintsOff(),
    toggleHints: () => {
      const on = panels ? panels.hints.isOff : hintsOff();
      panels?.hints.setOff(!on);
      return on;
    },
    quit: () => {
      host?.dispose();
      host = null;
      netHost = null;
      if (net) {
        void net.session.leave();
        room = null;
        window.history.replaceState(null, '', location.pathname);
      }
      hud?.dispose();
      hud = null;
      live?.dispose();
      live = null;
      dispersedFrom = null;
      if (panels) {
        for (const panel of Object.values(panels)) panel.dispose();
      }
      panels = null;
      selection = null;
      stopMapInput?.();
      stopMapInput = null;
      if (net) openMenu();
      else openLobby();
    },
  });
  const capitalOf = (state: GameState) => state.system.planets.find((p) => p.homeOf === ME)?.hex;
  const order = (command: Command) => host?.send(command);
  /** Projects by id, remembered from their opening: a resolved one is gone from the state. */
  const projectKinds = new Map<number, ProjectId>();
  const foldLeft = (open: boolean) => ui.style.setProperty('--map-left', open ? '332px' : '76px');
  // The right panel is 392 px wide, folded 48; 16 px from the edge and 12 px of air beside it.
  const foldRight = (open: boolean) => ui.style.setProperty('--map-right', open ? '420px' : '76px');
  foldLeft(true);
  foldRight(true);
  panels = {
    left: new LeftColumn(ui, viewer, (open) => {
      foldLeft(open);
      if (lastState) panels?.left.update(lastState);
    }),
    side: new SidePanel(
      ui,
      viewer,
      order,
      (open) => foldRight(open),
      (fleetId) => {
        const fleet = lastState?.fleets.find((f) => f.id === fleetId);
        if (!fleet || !lastState) return;
        selection = { fleetId, hex: fleet.hex };
        refreshSelection(lastState);
        rig.flyToHex(fleet.hex);
      },
    ),
    banner: new CrisisBanner(ui, locale, ME, order),
    finds: new FindOfferPanel(ui, locale, ME, order),
    hints: new HintCard(ui, viewer, (tab) => panels?.side.show(tab)),
    notes: new Notes(ui, locale, (h) => rig.flyToHex(h)),
    tooltip: new Tooltip(ui),
    card: new SelectionCard(
      ui,
      locale,
      order,
      () => {
        selection = null;
        if (lastState) refreshSelection(lastState);
      },
      () => scheme,
    ),
    units: new UnitsBar(ui, locale, ME, {
      select: (fleetId, add) => selectFleet(fleetId, add),
      fly: (fleetId) => {
        const fleet = lastState?.fleets.find((f) => f.id === fleetId);
        if (fleet) rig.flyToHex(fleet.hex);
      },
    }),
    minimap: new Minimap(ui, locale, ME, {
      lookAt: (x, z) => rig.flyTo(new Vector3(x, 0, z)),
      zoom: (factor) => rig.zoom(factor),
      home: () => {
        const capital = lastState && capitalOf(lastState);
        if (capital) rig.flyToHex(capital);
      },
    }),
  };

  /**
   * A fleet picked from the units bar (#154); `add` puts it with the ones picked
   * already, as a drag would (#111), or takes it out again.
   */
  const selectFleet = (fleetId: number, add: boolean) => {
    const fleet = lastState?.fleets.find((f) => f.id === fleetId);
    if (!fleet || !lastState) return;
    const was = selection?.fleetId != null ? (selection.group ?? [selection.fleetId]) : [];
    const group = add
      ? was.includes(fleetId)
        ? was.filter((id) => id !== fleetId)
        : [...was, fleetId]
      : [fleetId];
    const first = lastState.fleets.find((f) => f.id === group[0]);
    selection = first
      ? { fleetId: first.id, hex: first.hex, ...(group.length > 1 ? { group } : {}) }
      : null;
    refreshSelection(lastState);
  };

  /** The selection card follows the state: a fleet moves, its orders change. */
  const refreshSelection = (state: GameState) => {
    panels?.units.setSelected(
      selection?.fleetId != null ? (selection.group ?? [selection.fleetId]) : [],
    );
    if (!selection || !panels) {
      panels?.card.show(null);
      live?.setSelection(null, null);
      live?.setGroup([]);
      return;
    }
    const fleet =
      selection.fleetId !== null
        ? state.fleets.find((f) => f.id === selection!.fleetId)
        : undefined;
    if (selection.group) {
      // A fleet of the group that is gone (merged, destroyed) leaves it.
      const left = selection.group.filter((id) => state.fleets.some((f) => f.id === id));
      selection.group = left.length > 1 ? left : undefined;
    }
    if (selection.fleetId !== null && !fleet) selection = { fleetId: null, hex: selection.hex };
    live?.setGroup(selection.group ?? []);
    if (fleet) {
      selection.hex = fleet.hex;
      const card = fleetCard(state, fleet, viewer);
      const group = selection.group?.length ?? 0;
      panels.card.show({
        kind: 'fleet',
        owner: fleet.owner,
        card:
          group > 1
            ? {
                ...card,
                status: `${t(locale, 'ui.map.group', { n: group })} · ${card.status ?? card.meta}`,
              }
            : card,
      });
      live?.setSelection(fleet.hex, fleet.id);
    } else {
      panels.card.show({ kind: 'hex', info: describeHex(state, selection.hex, viewer) });
      live?.setSelection(selection.hex, null);
    }
  };

  /** A click picks the player's fleets on a hex in turn, or else the hex itself. */
  const pick = (h: Hex) => {
    if (!lastState) return;
    const mine = lastState.fleets.filter((f) => f.owner === ME && hexKey(f.hex) === hexKey(h));
    const current = mine.findIndex((f) => f.id === selection?.fleetId);
    const next = mine[(current + 1) % Math.max(1, mine.length)];
    selection = next ? { fleetId: next.id, hex: h } : { fleetId: null, hex: h };
    refreshSelection(lastState);
  };

  /** The picked fleet, when it is the player's own. */
  const pickedFleet = () =>
    selection?.fleetId != null
      ? lastState?.fleets.find((f) => f.id === selection!.fleetId && f.owner === ME)
      : undefined;
  const orderTo = (h: Hex) => {
    if (selection?.fleetId == null || !lastState || !inMap(lastState, h)) return;
    // A dragged group flies together (#111): one order per fleet.
    for (const fleetId of selection.group ?? [selection.fleetId]) {
      host?.send({ type: 'moveFleet', playerId: ME, fleetId, to: h });
    }
  };

  stopMapInput = bindMapInput({
    hover: (h, x, y) => {
      live?.setHover(h);
      const fleet = pickedFleet();
      if (h && lastState && inMap(lastState, h)) {
        const info = describeHex(lastState, h, viewer);
        // With a ship picked: its way there and how long (#162), before the click.
        const path =
          fleet && hexKey(fleet.hex) !== hexKey(h)
            ? // An unarmed ship goes round the enemy (#158): its way shows so.
              findPath(
                lastState.system,
                fleet.hex,
                h,
                isArmed(fleet) ? undefined : enemyHexes(lastState, ME),
              )
            : null;
        live?.setPreview(fleet && path ? [fleet.hex, ...path] : null);
        const go =
          !fleet || hexKey(fleet.hex) === hexKey(h)
            ? null
            : path
              ? t(locale, 'ui.map.goHere', {
                  time: clock(path.length * fleetTicksPerHex(fleet)),
                  how: t(
                    locale,
                    scheme === 'touchpad' ? 'ui.map.goHere.click' : 'ui.map.goHere.right',
                  ),
                })
              : t(locale, 'ui.map.goNo');
        panels?.tooltip.show(go ? { ...info, lines: [go, ...info.lines] } : info, x, y);
      } else {
        live?.setPreview(null);
        panels?.tooltip.hide();
      }
    },
    pick: (h) => {
      if (!lastState || !inMap(lastState, h)) return;
      // «Тачпад» (#162): with a ship picked, a click where none of the player's ships
      // stand sends it there; a click on it again lets it go.
      const fleet = pickedFleet();
      if (scheme === 'touchpad' && fleet) {
        const mine = lastState.fleets.filter((f) => f.owner === ME && hexKey(f.hex) === hexKey(h));
        if (!mine.length) return orderTo(h);
        if (mine.length === 1 && mine[0]!.id === fleet.id && !selection?.group) {
          selection = null;
          return refreshSelection(lastState);
        }
      }
      pick(h);
    },
    order: orderTo,
    box: (x0, y0, x1, y1) => {
      if (!lastState) return;
      const rect = labelRenderer.domElement.getBoundingClientRect();
      const [left, right] = [Math.min(x0, x1), Math.max(x0, x1)];
      const [top, bottom] = [Math.min(y0, y1), Math.max(y0, y1)];
      const inside = (h: Hex) => {
        const p = hexToWorld(h, 0.3).project(camera);
        const x = rect.left + ((p.x + 1) / 2) * rect.width;
        const y = rect.top + ((1 - p.y) / 2) * rect.height;
        return p.z < 1 && x >= left && x <= right && y >= top && y <= bottom;
      };
      const picked = lastState.fleets.filter((f) => f.owner === ME && inside(f.hex));
      if (!picked.length) return;
      selection = {
        fleetId: picked[0]!.id,
        hex: picked[0]!.hex,
        ...(picked.length > 1 ? { group: picked.map((f) => f.id) } : {}),
      };
      refreshSelection(lastState);
    },
    clear: () => {
      if (!selection) return false;
      selection = null;
      if (lastState) refreshSelection(lastState);
      return true;
    },
    home: () => {
      const capital = lastState && capitalOf(lastState);
      if (capital) rig.flyToHex(capital);
    },
    key: (e) => {
      if (panels?.notes.handleKey(e)) return true;
      if (panels?.side.handleKey(e)) return true;
      if (panels?.units.handleKey(e)) return true;
      if (e.code === 'BracketLeft') {
        panels?.left.toggle();
        return true;
      }
      return false;
    },
  });

  const onSnapshot = ({ state, events, history: skipped }: Snapshot) => {
    lastState = state;
    if (skipped) history.seed(skipped);
    history.record(state);
    if (serverMode && state.result && !sent) {
      sent = true;
      reported = report(state);
      // The leaderboard beside the lobby picks the match up next time.
      leaders = null;
    }
    if (!shown) {
      shown = true;
      const capital = capitalOf(state);
      const far = dev && params.get('camera') === 'far';
      showSystem(state, setup, far ? null : (capital ?? null));
      live = new LiveLayer(
        ME,
        locale,
        state.system.mapRadius,
        (sector) => view?.tintSector(sector),
        (visible, explored) => view?.setFog(visible, explored),
      );
      scene.add(live.root);
      document.body.dataset.screen = 'match';
    }
    live?.update(state);
    // Hull lost lately (#156): a red mark on the unit, its row and its badge on the map.
    const hit = hits.update(state, ME);
    live?.setHit(hit);
    if (view && state.exploration !== dispersedFrom) {
      // Clouds scanned to the end fade from the map (#103).
      dispersedFrom = state.exploration;
      const left = state.exploration.findsLeft;
      view.disperseClouds(
        new Set(state.system.clouds.filter((c) => (left[c.id] ?? 0) <= 0).map((c) => c.id)),
      );
    }
    hud?.update(state);
    refreshSelection(state);
    if (panels) {
      for (const e of events) if (e.type === 'projectOpened') projectKinds.set(e.projectId, e.kind);
      for (const e of events) if (e.type === 'volley') live?.fire(e.shots);
      const notes = events.flatMap((e) => noteFor(e, state, viewer, projectKinds) ?? []);
      if (notes.length) panels.notes.add(notes, performance.now());
      panels.left.update(state);
      panels.side.update(state);
      panels.units.update(state);
      panels.units.setHit(hit);
      panels.side.setHit(hit);
      panels.banner.update(state);
      panels.finds.update(state);
      panels.hints.update(state, events);
    }
    const crisis = state.crises.find((c) => c.warned.includes(ME));
    panels?.minimap.update(state, crisis ? crisis.sector : undefined, performance.now());
    document.body.dataset.tick = String(state.tick);
    if (state.result) document.body.dataset.ended = '1';
  };
  const onError = (message: string) => console.error(`simulation: ${message}`);
  if (net) {
    netHost = new NetHost(net.start, net.session, onSnapshot, onError);
    host = netHost;
  } else {
    host = new MatchHost(setup, onSnapshot, onError, dev ? Number(params.get('at') ?? 0) : 0);
  }
  // Dev: orders and state from the console and browser scripts, as the player's own (#96).
  if (dev) {
    (window as unknown as { sdDev: unknown }).sdDev = {
      send: (command: Command) => host?.send(command),
      state: () => lastState,
      look: (hex: Hex, zoom = 1) => {
        rig.flyToHex(hex);
        rig.zoom(zoom);
      },
      select: (fleetId: number) => {
        const fleet = lastState?.fleets.find((f) => f.id === fleetId);
        if (!fleet || !lastState) return;
        selection = { fleetId, hex: fleet.hex };
        refreshSelection(lastState);
      },
    };
  }
  // Dev: any speed from the start, for the browser match test (#60).
  const speed = dev && !net ? Number(params.get('speed') ?? 0) : 0;
  if (speed > 1) host.setSpeed(Math.min(MAX_DEV_SPEED, speed));
}

const inMap = (state: GameState, h: Hex) =>
  Math.max(Math.abs(h.q), Math.abs(h.r), Math.abs(h.q + h.r)) <= state.system.mapRadius;

interface MapInput {
  hover(h: Hex | null, x: number, y: number): void;
  pick(h: Hex): void;
  order(h: Hex): void;
  /** A left drag (#111): the player's fleets inside the screen rectangle. */
  box(x0: number, y0: number, x1: number, y1: number): void;
  /** Esc: drops the selection; false when there was none, so Esc opens the menu. */
  clear(): boolean;
  home(): void;
  /** Panel keys (log, folding) come first; true when the key was used. */
  key(e: KeyboardEvent): boolean;
}

/**
 * Mouse and keys on the map: a click selects, a left drag selects the
 * player's fleets in a rectangle (#111), a right click orders the selection,
 * Esc drops it, Home flies home. The map pans with the middle button and WASD.
 */
function bindMapInput(on: MapInput): () => void {
  const element = labelRenderer.domElement;
  let down: { x: number; y: number } | null = null;
  let lastKey = '';
  const band = document.createElement('div');
  band.className = 'map-band';
  band.hidden = true;
  document.body.append(band);
  const onDown = (e: PointerEvent) => {
    if (e.button !== 0) return;
    down = { x: e.clientX, y: e.clientY };
    element.setPointerCapture(e.pointerId);
  };
  const onUp = (e: PointerEvent) => {
    if (e.button !== 0 || !down) return;
    const from = down;
    const moved = Math.hypot(e.clientX - from.x, e.clientY - from.y);
    down = null;
    band.hidden = true;
    if (moved > 5) {
      on.box(from.x, from.y, e.clientX, e.clientY);
      return;
    }
    const h = rig.hexAt(e.clientX, e.clientY);
    if (h) on.pick(h);
  };
  const onMenu = (e: MouseEvent) => {
    e.preventDefault();
    const h = rig.hexAt(e.clientX, e.clientY);
    if (h) on.order(h);
  };
  const onMove = (e: PointerEvent) => {
    if (down && Math.hypot(e.clientX - down.x, e.clientY - down.y) > 5) {
      band.hidden = false;
      band.style.left = `${Math.min(down.x, e.clientX)}px`;
      band.style.top = `${Math.min(down.y, e.clientY)}px`;
      band.style.width = `${Math.abs(e.clientX - down.x)}px`;
      band.style.height = `${Math.abs(e.clientY - down.y)}px`;
    }
    const h = rig.hexAt(e.clientX, e.clientY);
    const key = h ? hexKey(h) : '';
    if (key !== lastKey || h) on.hover(h, e.clientX, e.clientY);
    lastKey = key;
  };
  const onLeave = () => {
    lastKey = '';
    on.hover(null, 0, 0);
  };
  const onKey = (e: KeyboardEvent) => {
    const target = e.target as HTMLElement | null;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'SELECT')) return;
    if (on.key(e)) e.stopImmediatePropagation();
    else if (e.code === 'Escape' && on.clear()) e.stopImmediatePropagation();
    else if (e.code === 'Home') on.home();
  };
  element.addEventListener('pointerdown', onDown);
  element.addEventListener('pointerup', onUp);
  element.addEventListener('contextmenu', onMenu);
  element.addEventListener('pointermove', onMove);
  element.addEventListener('pointerleave', onLeave);
  window.addEventListener('keydown', onKey, { capture: true });
  return () => {
    element.removeEventListener('pointerdown', onDown);
    element.removeEventListener('pointerup', onUp);
    element.removeEventListener('contextmenu', onMenu);
    element.removeEventListener('pointermove', onMove);
    element.removeEventListener('pointerleave', onLeave);
    window.removeEventListener('keydown', onKey, { capture: true });
    band.remove();
  };
}

async function saveLog(setup: MatchSetup) {
  if (!host) return;
  const log = await host.log();
  const blob = new Blob([JSON.stringify(log)], { type: 'application/json' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `space-dominance-${setup.seed}-${new Date().toISOString().slice(0, 16).replace(':', '')}.json`;
  link.click();
  URL.revokeObjectURL(link.href);
}

window.addEventListener('keydown', (event) => hud?.handleKey(event));

// ---------- loop ----------
function resize() {
  const { innerWidth: w, innerHeight: h } = window;
  renderer.setSize(w, h);
  labelRenderer.setSize(w, h);
  camera.aspect = w / h;
  camera.updateProjectionMatrix();
  outline.setSize(renderer.domElement.width, renderer.domElement.height);
}
window.addEventListener('resize', resize);

let framesDrawn = 0;
let lastFrame = 0;
function frame(timeMs: number) {
  const dt = Math.min(0.1, (timeMs - lastFrame) / 1000);
  lastFrame = timeMs;
  const cpuStart = performance.now();
  renderer.info.reset();
  rig.update(dt);
  controls.update();
  view?.update(timeMs / 1000);
  // Thicker lines up close, thinner from afar, so the map stays readable.
  const distance = camera.position.distanceTo(controls.target);
  live?.frame(dt, timeMs / 1000, distance);
  if (panels) {
    panels.minimap.drawView(rig.footprint(TOPBAR_HEIGHT));
    panels.notes.frame(panels.banner.bottom, performance.now());
  }
  outline.setThickness(Math.min(2.5, Math.max(1, 40 / distance)) * renderer.getPixelRatio());
  outline.render(scene, camera);
  labelRenderer.render(scene, camera);
  if (++framesDrawn === 3) document.body.dataset.ready = 'true';
  if (bench) {
    bench.sample(performance.now() - cpuStart, timeMs);
    if (bench.frames === BENCH_FRAMES) {
      const { calls, triangles } = renderer.info.render;
      const result = bench.result(calls, triangles);
      document.body.dataset.bench = JSON.stringify(result);
      // On screen too, for a run on a real graphics card (dev tool, so plain text).
      const panel = document.createElement('pre');
      panel.className = 'sd-panel sd-bench';
      panel.textContent = Object.entries(result)
        .map(([k, v]) => `${k}: ${v}`)
        .join('\n');
      ui.append(panel);
    }
  }
}

// ---------- render benchmark (dev tool, #55) ----------
const BENCH_FRAMES = 300;
let bench: BenchMeter | null = null;

/** `?dev=1&bench=300`: a busy synthetic map, the frame cost measured over 300 frames. */
function startBench(ships: number) {
  const setup = toSetup({ ...choice, players: 4 });
  const base = createGame({ seed: setup.seed, players: setup.seats });
  showSystem(base, setup, null);
  live = new LiveLayer(ME, locale, base.system.mapRadius, (sector) => view?.tintSector(sector));
  scene.add(live.root);
  let tick = 0;
  live.update(benchState(base, ships, tick));
  setInterval(() => live?.update(benchState(base, ships, ++tick)), 1000 / TICKS_PER_SECOND);
  bench = new BenchMeter(ships);
  document.body.dataset.screen = 'bench';
}

resize();
// Every renderer.render call of the outline passes adds to the count; reset once per frame.
renderer.info.autoReset = false;
/** What the page opens with: the benchmark, a match at once, or the lobby. */
function start() {
  if (dev && params.get('bench')) {
    startBench(Number(params.get('bench')) || 300);
  } else if (params.get('autostart') === '1') {
    document.documentElement.lang = locale;
    const setup = toSetup(choice);
    // Dev tool: a bot plays your seat too, so a screenshot shows a lively match.
    // `spectate=raider` picks the bot (#154: one with warships for the units bar).
    const asked = dev ? params.get('spectate') : null;
    const bot = (['trader', 'raider', 'scientist', 'cooperator'] as const).find((b) => b === asked);
    const spectate = asked === '1' ? 'cooperator' : bot;
    startMatch(
      spectate
        ? {
            ...setup,
            seats: setup.seats.map((s, i) => (i === 0 ? { ...s, control: spectate } : s)),
          }
        : setup,
    );
  } else if (serverMode && params.get('room')) {
    // A link to a room (#139): straight into it.
    const code = params.get('room')!.toUpperCase();
    RoomSession.join(code)
      .then((view) => enterRoom(view.id))
      .catch((error: unknown) => {
        netNote = netReason(error);
        window.history.replaceState(null, '', location.pathname);
        openFind();
      });
  } else if (params.get('screen') === 'lobby' || params.get('seed')) {
    openLobby();
  } else {
    openMenu();
  }
}

// The playtest build needs a signed-in player (#61, #85): the developer's site signs in.
if (serverMode) {
  void whoAmI()
    .catch(() => null)
    .then((me) => {
      if (!me) return goSignIn();
      account = me;
      start();
    });
} else {
  start();
}
renderer.setAnimationLoop(frame);
